import React from 'react'
import {
    useFloating,
    useInteractions,
    FloatingPortal,
} from '@floating-ui/react'
import styled, { css, keyframes } from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { ErrorDisplayLogic, type ErrorDisplayProps } from './logic'
import { useAppContext } from '~/app-context'

function ErrorDisplay(props: ErrorDisplayProps) {
    const { services } = useAppContext()
    const { logic, state } = useLogic(ErrorDisplayLogic, {
        ...props,
        services,
    })

    const { refs, context } = useFloating({
        open: state.errors.length > 0,
    })

    const { getFloatingProps } = useInteractions([])

    // Don't render if there are no errors
    if (state.errors.length === 0) {
        return null
    }

    const errorContent = (
        <ErrorContainer ref={refs.setFloating} {...getFloatingProps()}>
            {state.errors.map((error) => (
                <ErrorItem key={error.id}>
                    <ErrorContent>
                        <ErrorMessage>{error.message}</ErrorMessage>
                        <ErrorActions>
                            <ClearButton
                                onClick={() => logic.clearError(error.id)}
                                aria-label="Clear this error"
                            >
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9 3L3 9M3 3L9 9"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </ClearButton>
                        </ErrorActions>
                    </ErrorContent>
                </ErrorItem>
            ))}

            {state.errors.length > 1 && (
                <ClearAllButton
                    onClick={() => logic.clearAllErrors()}
                    aria-label="Clear all errors"
                >
                    Clear All ({state.errors.length})
                </ClearAllButton>
            )}
        </ErrorContainer>
    )

    return <FloatingPortal>{errorContent}</FloatingPortal>
}

export default ErrorDisplay

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`

const fadeOut = keyframes`
    from {
        opacity: 1;
        transform: translateY(0);
    }
    to {
        opacity: 0;
        transform: translateY(20px);
    }
`

const ErrorContainer = styled.div`
    position: fixed;
    bottom: ${({ theme }) => theme.spacing[4]};
    right: ${({ theme }) => theme.spacing[4]};
    z-index: 10000001; // Higher than modal (1000) but not blocking
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
    max-width: 400px;
    width: auto;
    min-width: 300px;
    pointer-events: auto;
`

const ErrorItem = styled.div`
    background-color: ${({ theme }) => theme.colors.info};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    animation: ${fadeIn} ${({ theme }) => theme.transitions.normal} ease-out;
    overflow: hidden;

    &:hover {
        box-shadow: ${({ theme }) => theme.shadows.xl};
    }
`

const ErrorContent = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing[3]};
    padding: ${({ theme }) => theme.spacing[3]};
`

const ErrorMessage = styled.div`
    flex: 1;
    color: black;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    line-height: ${({ theme }) => theme.typography.lineHeight.normal};
    word-wrap: break-word;
`

const ErrorActions = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
`

const ClearButton = styled.button`
    background: none;
    border: none;
    padding: ${({ theme }) => theme.spacing[1]};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    cursor: pointer;
    color: ${({ theme }) => theme.colors.error[600]};
    transition: all ${({ theme }) => theme.transitions.fast};
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    min-height: 24px;

    &:hover {
        background-color: ${({ theme }) => theme.colors.error[100]};
        color: ${({ theme }) => theme.colors.error[800]};
    }

    &:focus {
        outline: 2px solid ${({ theme }) => theme.colors.error[300]};
        outline-offset: 2px;
    }
`

const ClearAllButton = styled.button`
    background-color: ${({ theme }) => theme.colors.greyScale[100]};
    border: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
    cursor: pointer;
    color: ${({ theme }) => theme.colors.greyScale[700]};
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    transition: all ${({ theme }) => theme.transitions.fast};
    align-self: flex-end;
    margin-top: ${({ theme }) => theme.spacing[1]};

    &:hover {
        background-color: ${({ theme }) => theme.colors.greyScale[200]};
        border-color: ${({ theme }) => theme.colors.greyScale[400]};
        color: ${({ theme }) => theme.colors.greyScale[900]};
    }

    &:focus {
        outline: 2px solid ${({ theme }) => theme.colors.greyScale[400]};
        outline-offset: 2px;
    }
`
